import type { Exchange, Operation } from '@urql/core';

import { filter, fromPromise, fromValue, map, mergeMap, onPush, pipe, share, takeUntil } from 'wonka';

/**
 * Holds GraphQL requests back while something must happen with no other request on the wire. An
 * account switch is the case: a response that left on the old session would write that session's
 * cookie back over the new one. `hold()` stops new operations going out and resolves once the ones
 * already sent have answered, or after `DEFAULT_MAX_WAIT_MS` so a hung request cannot keep the page
 * waiting. An operation is answered at its first result, which carries the response headers; for a
 * subscription that is the answer to its registration request. Operations sharing a key are each
 * counted, so two identical mutations both have to answer, and a teardown ends every request under
 * its key. An operation whose context sets `passesOperationGate` goes out regardless, and
 * `release()` sends what was held.
 */
export interface OperationGate {
  exchange: Exchange;
  hold: () => Promise<void>;
  release: () => void;
}

export const DEFAULT_MAX_WAIT_MS = 10_000;

const isNetworkRequest = (operation: Operation) => operation.kind === 'query' || operation.kind === 'mutation' || operation.kind === 'subscription';

export const createOperationGate = (): OperationGate => {
  const inFlight = new Map<number, number>();
  const settledListeners = new Set<() => void>();
  let opened: { promise: Promise<void>; resolve: () => void } | null = null;

  const answered = new WeakSet<Operation>();

  const notifyIfSettled = () => {
    if (inFlight.size === 0) {
      settledListeners.forEach(listener => listener());
    }
  };

  const markSent = (key: number) => inFlight.set(key, (inFlight.get(key) ?? 0) + 1);

  const markAnswered = (operation: Operation) => {
    const count = inFlight.get(operation.key);
    if (count === undefined || answered.has(operation)) {
      return;
    }
    answered.add(operation);
    if (count > 1) {
      inFlight.set(operation.key, count - 1);
      return;
    }
    inFlight.delete(operation.key);
    notifyIfSettled();
  };

  const markTornDown = (key: number) => {
    if (inFlight.delete(key)) {
      notifyIfSettled();
    }
  };

  const exchange: Exchange = ({ forward }) => (operations$) => {
    const shared$ = pipe(operations$, share);
    const teardownOf = (key: number) => pipe(shared$, filter(operation => operation.kind === 'teardown' && operation.key === key));

    const gated$ = pipe(
      shared$,
      mergeMap((operation) => {
        if (!opened || operation.kind === 'teardown' || operation.context.passesOperationGate) {
          return fromValue(operation);
        }

        return pipe(fromPromise(opened.promise), map(() => operation), takeUntil(teardownOf(operation.key)));
      }),
      onPush((operation) => {
        if (isNetworkRequest(operation)) {
          markSent(operation.key);
        }
        else if (operation.kind === 'teardown') {
          markTornDown(operation.key);
        }
      }),
    );

    return pipe(forward(gated$), onPush(result => markAnswered(result.operation)));
  };

  const hold = () => {
    if (!opened) {
      let resolve: () => void = () => {};
      const promise = new Promise<void>((done) => {
        resolve = done;
      });
      opened = { promise, resolve };
    }

    if (inFlight.size === 0) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const done = () => {
        settledListeners.delete(done);
        resolve();
      };
      settledListeners.add(done);
      setTimeout(done, DEFAULT_MAX_WAIT_MS);
    });
  };

  const release = () => {
    opened?.resolve();
    opened = null;
  };

  return { exchange, hold, release };
};
